import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { ListSkeleton } from "../components/Skeleton";

interface AuditEntry {
  id: number;
  createdAt: string;
  actionType: string;
  entityType: string;
  entityUuid: string | null;
  metadata: Record<string, unknown>;
  prevHash: string | null;
  entryHash: string;
  user: { uuid: string; fullName: string; email: string; erased: boolean } | null;
}

interface ListResponse {
  entries: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

const ACTION_LABEL: Record<string, string> = {
  USER_SIGNED_UP: "User signed up",
  USER_LOGGED_IN: "User logged in",
  ORG_SEEDED: "Organisation seeded",
  EVIDENCE_CREATED: "Evidence created",
  EVIDENCE_UPDATED: "Evidence updated",
  EVIDENCE_DELETED: "Evidence deleted",
  EVIDENCE_FILE_ADDED: "File added",
  EVIDENCE_FILE_REMOVED: "File removed",
  EVIDENCE_TRANSITIONED: "Evidence status changed",
  EVIDENCE_LINKED_TO_REQUIREMENT: "Evidence linked",
  EVIDENCE_UNLINKED_FROM_REQUIREMENT: "Evidence unlinked",
  NC_RAISED: "Nonconformity raised",
  NC_TRANSITIONED: "Nonconformity status changed",
  NC_UPDATED: "Nonconformity updated",
  CAPA_CREATED: "Corrective action created",
  CAPA_TRANSITIONED: "Corrective action status changed",
  CAPA_UPDATED: "Corrective action updated",
  MEMBER_INVITED: "Member invited",
  MEMBER_ACCEPTED_INVITATION: "Member accepted invitation",
  MEMBER_DECLINED_INVITATION: "Member declined invitation",
  MEMBER_REMOVED: "Member removed",
};

const ENTITY_LABEL: Record<string, string> = {
  organisations: "Organisation",
  users: "User",
  evidence_vault: "Evidence",
  evidence_files: "File",
  nonconformities: "Nonconformity",
  corrective_actions: "Corrective action",
  user_organisation_memberships: "Membership",
};

const formatDateTimeUk = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

export function AuditLog() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [actionFilter, setActionFilter] = useState<string>("");
  const [entityFilter, setEntityFilter] = useState<string>("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setData(null);
    const params = new URLSearchParams();
    if (actionFilter) params.set("actionType", actionFilter);
    if (entityFilter) params.set("entityType", entityFilter);
    params.set("limit", "100");
    api<ListResponse>(`/audit-log?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setErrors(formatApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [actionFilter, entityFilter]);

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const distinctActions = Array.from(
    new Set((data?.entries ?? []).map((e) => e.actionType)),
  ).sort();
  const distinctEntities = Array.from(
    new Set((data?.entries ?? []).map((e) => e.entityType)),
  ).sort();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Audit log</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Tamper-evident, append-only history of every action in this organisation. Each entry is
          hash-chained to the previous one — the `prev_hash` of each row is the `entry_hash` of the
          entry before it.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="block text-xs">
          <span className="block text-slate-500 dark:text-slate-400">Action</span>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
          >
            <option value="">All actions</option>
            {distinctActions.map((a) => (
              <option key={a} value={a}>
                {ACTION_LABEL[a] ?? a}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs">
          <span className="block text-slate-500 dark:text-slate-400">Entity</span>
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
          >
            <option value="">All entities</option>
            {distinctEntities.map((e) => (
              <option key={e} value={e}>
                {ENTITY_LABEL[e] ?? e}
              </option>
            ))}
          </select>
        </label>
      </div>

      {errors.length > 0 ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}

      {data === null ? (
        <ListSkeleton rows={8} />
      ) : data.entries.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">No entries match your filters.</p>
      ) : (
        <>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Showing {data.entries.length} of {data.total} entries
          </p>
          <ol className="space-y-1">
            {data.entries.map((entry) => {
              const isOpen = expanded.has(entry.id);
              const userDisplay = entry.user
                ? entry.user.erased
                  ? "Anonymous (erased)"
                  : entry.user.fullName
                : "Anonymous";
              return (
                <li
                  key={entry.id}
                  className="rounded border border-slate-200 bg-white text-sm dark:border-slate-700 dark:bg-slate-800"
                >
                  <button
                    type="button"
                    onClick={() => toggle(entry.id)}
                    aria-expanded={isOpen}
                    className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {ACTION_LABEL[entry.actionType] ?? entry.actionType}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        {ENTITY_LABEL[entry.entityType] ?? entry.entityType}
                        {entry.entityUuid ? ` · ${entry.entityUuid.slice(0, 8)}…` : ""} · by{" "}
                        {userDisplay}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">
                      {formatDateTimeUk(entry.createdAt)}
                    </span>
                  </button>
                  {isOpen ? (
                    <div className="border-t border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900">
                      <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-slate-700 dark:text-slate-300">
                        {JSON.stringify(entry.metadata, null, 2)}
                      </pre>
                      <p className="mt-2 font-mono text-[10px] text-slate-400">
                        prev_hash:{" "}
                        {entry.prevHash ? entry.prevHash.slice(0, 16) + "…" : "NULL (chain root)"}
                      </p>
                      <p className="font-mono text-[10px] text-slate-400">
                        entry_hash: {entry.entryHash.slice(0, 16)}…
                      </p>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </>
      )}
    </div>
  );
}
